import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Source: docs/scenario_pages_plan.md EP06 — "Caffeine & Sleep".

export const caffeineDerek: Scenario = {
  slug: 'caffeine-derek',
  series: 'Caffeine & Sleep',
  title: "He could drink coffee at 9pm and fall asleep. That was the problem.",
  socialHook:
    "Derek, 41: he thought caffeine did not affect him because he could fall asleep after coffee. His results showed the opposite problem: he could not feel the damage it was doing to sleep.",
  meta: {
    description:
      "A fictional case study: Derek drank 3-4 coffees a day and thought he slept fine. His CYP1A2 and ADORA2A results explain why afternoon caffeine could disrupt deep sleep even when he never felt wired.",
  },

  persona: {
    name: 'Derek',
    age: 41,
    sex: 'Male',
    ancestry: 'White European',
    occupation: 'Software engineer',
    chiefConcern:
      "For the past year he wakes between 2 and 4am with his brain racing. He falls asleep easily, but his sleep is broken, his afternoon energy crashes, and his anxiety is higher than usual.",
    familyHistory: "Father: type 2 diabetes. No family history of sleep disorders.",
  },

  clinicalPicture: {
    symptoms: [
      "Wakes between 2 and 4am most nights, brain racing — falls asleep again after 30–60 minutes",
      "Afternoon energy crash, typically 2–4pm, most days",
      "Elevated anxiety over the past year — background tension rather than panic",
      "Drinks 3–4 cups of coffee per day, last cup typically around 3–4pm",
    ],
    labs: [
      {
        name: "Morning cortisol (stress hormone measured in the blood)",
        value: "19 mcg/dL",
        refRange: "6–18 mcg/dL",
        flag: "high",
      },
      {
        name: "Sleep tracker deep sleep percentage",
        value: "8%",
        refRange: "15–25% of total sleep",
        flag: "low",
      },
    ],
    medications: ["No prescription medications"],
    supplements: ["None"],
    lifestyle: [
      "Coffee: 3–4 cups per day, first at 7am, last typically at 3–4pm",
      "Sleep: 7.5 hours in bed, falls asleep within 10 minutes, wakes 2–4am and lies awake 30–60 minutes",
      "Exercise: gym 2x/week (evenings), sedentary desk job otherwise",
      "Diet: regular meals, moderate sugar, no major dietary restrictions",
      "Alcohol: 3–5 drinks per week, mostly weekends",
      "Non-smoker",
    ],
  },

  genetics: [
    {
      gene: "CYP1A2",
      variant: "Slow metabolizer variant",
      rsid: "rs762551",
      genotype: "AA (two slow copies)",
      plainMeaning:
        "Derek clears caffeine slowly, so an afternoon coffee can still be active after he has gone to bed.",
      technicalDetail:
        "CYP1A2 is the liver enzyme responsible for breaking down caffeine. Fast metabolizers clear it quickly — half of it is gone within 2–3 hours. The AA genotype marks Derek as a slow metabolizer, where the half-life extends to 6–9 hours. A 3pm coffee still has roughly 50% of its caffeine circulating at 9–10pm, and 25–35% at midnight. This residual load elevates cortisol and suppresses deep sleep architecture regardless of whether he feels alert.",
    },
    {
      gene: "ADORA2A",
      variant: "Reduced adenosine sensitivity variant",
      rsid: "rs5751876",
      genotype: "TT (two copies of the variant)",
      plainMeaning:
        "Derek's brain does not feel much of caffeine's alerting effect, but the sleep-disrupting effects can still happen.",
      technicalDetail:
        "Caffeine works by blocking adenosine receptors — adenosine is the molecule that accumulates during the day and creates the feeling of sleepiness. ADORA2A encodes the main adenosine receptor. The TT variant reduces receptor sensitivity, which is why caffeine's subjective alerting effect barely registers for Derek. Critically, this variant does nothing to block caffeine's cortisol-raising effects or its suppression of deep sleep (slow-wave sleep) — those pathways are independent. His 'immunity' was real in one narrow sense, and completely absent in the ways that matter for sleep.",
    },
  ],

  beats: [
    {
      type: "narrative",
      heading: "Twenty years of the wrong clue",
      body: "Derek has drunk 3–4 coffees a day since college. He learned early that he could drink espresso after dinner and still fall asleep within an hour. That became the proof that caffeine did not affect him. So when the 3am waking started, then the afternoon crashes, then the background anxiety, coffee did not seem like a suspect. It felt like the one thing he already understood.",
    },
    {
      type: "narrative",
      heading: "Feeling fine was not proof",
      body: "Caffeine can affect the body in more than one way. It can block sleepiness signals, and it can raise stress hormones. Those effects do not always feel the same. A person can miss the 'wired' feeling and still have caffeine circulating, raising cortisol, and lowering sleep quality. Derek's main clue was misleading: falling asleep easily did not mean the caffeine was gone.",
    },
    {
      type: "reveal",
      heading: "His afternoon coffee was still there at midnight",
      body: "The issue was not bedtime coffee. It was afternoon caffeine that never cleared. Derek's CYP1A2 result means he metabolizes caffeine slowly, so a 3pm coffee can still leave a meaningful amount in his system at midnight. That leftover caffeine can keep cortisol higher than it should be and reduce deep sleep, even if he falls asleep fast. His tracker showing 8% deep sleep fits that pattern.",
    },
    {
      type: "reveal",
      heading: "He missed the warning signal",
      body: "Derek's ADORA2A result explains why he never felt wired. His brain is less sensitive to caffeine's alerting signal, so he can drink coffee without noticing much. But that does not protect his sleep. The cortisol and deep-sleep effects can still happen, especially when CYP1A2 keeps caffeine around for hours. His 'immunity' was not immunity. It was a missing warning light.",
    },
    {
      type: "action",
      heading: "Five things to try or discuss",
      items: [
        "Move the caffeine cutoff to noon. For a slow metabolizer, the usual 'no coffee after 2pm' rule may be too late.",
        "Reduce to 1–2 cups in the morning only. Lower volume means a lower total caffeine and cortisol load.",
        "Consider a 14-day caffeine-free trial to find his true baseline. Withdrawal can cause headaches, fatigue, and low mood in the first few days, but the comparison can be useful.",
        "Support cortisol rhythm with morning exercise, consistent wake times, and less screen or news exposure in the 30 minutes before bed.",
        "Recheck morning cortisol after about 6 weeks without afternoon caffeine. That gives him and his family doctor a concrete data point.",
      ],
    },
  ],

  extraBeats: [
    {
      type: "checkin" as const,
      heading: "Derek's 14-day caffeine experiment",
      goalLabel: "Sleep quality, energy & anxiety",
      caption:
        "Derek cut afternoon caffeine on day 1, dropped to one morning coffee on day 5, and went fully caffeine-free on day 10. The first few days were rough, but the sleep improvement from day 9 onward showed his 'caffeine immunity' was the wrong story.",
      days: [
        {
          day: 1,
          scores: [
            { label: "Sleep quality", value: 4 },
            { label: "Anxiety", value: 7 },
            { label: "Energy", value: 5 },
          ],
          note: "Cut afternoon coffee. No obvious difference yet.",
          milestone: "Cut afternoon caffeine",
        },
        {
          day: 2,
          scores: [
            { label: "Sleep quality", value: 3 },
            { label: "Anxiety", value: 7 },
            { label: "Energy", value: 4 },
          ],
          note: "Headache started mid-afternoon. Woke at 3am again.",
        },
        {
          day: 3,
          scores: [
            { label: "Sleep quality", value: 3 },
            { label: "Anxiety", value: 8 },
            { label: "Energy", value: 3 },
          ],
          note: "Worst day so far. Headache, low energy, irritable. Classic withdrawal.",
        },
        {
          day: 4,
          scores: [
            { label: "Sleep quality", value: 4 },
            { label: "Anxiety", value: 7 },
            { label: "Energy", value: 4 },
          ],
          note: "Headache easing off. Still woke in the night but only briefly.",
        },
        {
          day: 5,
          scores: [
            { label: "Sleep quality", value: 5 },
            { label: "Anxiety", value: 6 },
            { label: "Energy", value: 5 },
          ],
          note: "Down to one coffee at 8am. Morning was fine — did not miss the afternoon one as much as expected.",
          milestone: "Down to 1 morning coffee",
        },
        {
          day: 6,
          scores: [
            { label: "Sleep quality", value: 5 },
            { label: "Anxiety", value: 6 },
            { label: "Energy", value: 5 },
          ],
          note: "Slept until 4am before waking — an hour longer than usual.",
        },
        {
          day: 7,
          scores: [
            { label: "Sleep quality", value: 6 },
            { label: "Anxiety", value: 5 },
            { label: "Energy", value: 6 },
          ],
          note: "Afternoon crash less severe. Background tension lower than it has been.",
        },
        {
          day: 8,
          scores: [
            { label: "Sleep quality", value: 5 },
            { label: "Anxiety", value: 6 },
            { label: "Energy", value: 5 },
          ],
          note: "Bit of a dip today. Stressful work deadline — woke briefly at 2am.",
        },
        {
          day: 9,
          scores: [
            { label: "Sleep quality", value: 7 },
            { label: "Anxiety", value: 5 },
            { label: "Energy", value: 6 },
          ],
          note: "Slept through until 5:30am. That has not happened in months.",
        },
        {
          day: 10,
          scores: [
            { label: "Sleep quality", value: 7 },
            { label: "Anxiety", value: 4 },
            { label: "Energy", value: 7 },
          ],
          note: "Fully caffeine-free today. Surprisingly not as bad as expected.",
          milestone: "Fully caffeine-free",
        },
        {
          day: 11,
          scores: [
            { label: "Sleep quality", value: 8 },
            { label: "Anxiety", value: 4 },
            { label: "Energy", value: 7 },
          ],
          note: "Best sleep in a year. Woke feeling like I had actually rested.",
        },
        {
          day: 12,
          scores: [
            { label: "Sleep quality", value: 7 },
            { label: "Anxiety", value: 3 },
            { label: "Energy", value: 7 },
          ],
          note: "The afternoon crash is basically gone. I did not notice it was 3pm.",
        },
        {
          day: 13,
          scores: [
            { label: "Sleep quality", value: 8 },
            { label: "Anxiety", value: 3 },
            { label: "Energy", value: 8 },
          ],
          note: "Slept 7 hours without waking. Anxiety is the lowest it has been all year.",
        },
        {
          day: 14,
          scores: [
            { label: "Sleep quality", value: 8 },
            { label: "Anxiety", value: 3 },
            { label: "Energy", value: 8 },
          ],
          note: "Two weeks in. The 3am waking is gone. I thought this was just getting older. It was caffeine.",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: "caffeine-derek",
    label: "Explore your caffeine metabolism genetics in Coach",
  },
}
